<script lang="ts">
  import clsx from 'clsx';
  import { KeyboardArrowDown } from '@athena-lib/Icons';
  import { HeadingXSmall, Paragraph, Text } from '@athena-lib/Text';
  import { Button } from '@athena-lib/Button';
  import { getLocalizationContext } from '@athena-lib/i18n';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import TrustPilotStars from '@athena-lib/TrustPilotStars/TrustPilotStars.svelte';
  import Image from '@athena-lib/Image/Image.svelte';
  import type { TrustPilotProductReviewListProps } from './types';

  const moreDetailsShowing: number[] = $state([]);

  const localizationContext = getLocalizationContext();

  const {
    apiResponse,
    heading,
    sku,
    loadMoreButtonColor,
    fetchProductReviews,
  }: TrustPilotProductReviewListProps = $props();
  const reviews = $state(apiResponse.reviews);

  function toggleMoreDetails(pos: number) {
    const index = moreDetailsShowing.indexOf(pos);
    if (index !== -1) {
      moreDetailsShowing.splice(index, 1);
    } else {
      moreDetailsShowing.push(pos);
    }
  }

  const translations = getCommonTranslations();
  let page = $state(1);
  async function loadMoreReviews() {
    page += 1;
    const newReviews = await fetchProductReviews({
      page: page.toString(),
      sku,
      isoCountryCode: localizationContext.countryCode,
    });
    if (newReviews) {
      reviews.push(...newReviews.reviews);
    }
  }
</script>

<div>
  {#if heading}
    <HeadingXSmall size="medium">{heading}</HeadingXSmall>
  {/if}
  <div
    class="mb-gutter flex items-center justify-between text-st-black md:justify-start md:gap-gutter"
  >
    <div class="flex items-center gap-gutter">
      <div class="text-nowrap">
        <Text element="span" bold={true} class="text-lg">{apiResponse.starsAverage}</Text>
        <Text element="span" class="text-sm">/ 5</Text>
      </div>
    </div>
    <div>
      <Text element="span">
        {apiResponse.allReviews}
        {translations.reviews.toLowerCase()}
      </Text>
    </div>
    <div class="flex h-5 justify-start">
      <Image loading="lazy" src={apiResponse.tpLogo} alt="Trustpilot logo" />
    </div>
  </div>

  <ul class="flex flex-wrap">
    {#each reviews as review, pos (pos)}
      <li
        class="flex-shrink-0 basis-full border-b border-st-gray-200 py-gutter md:basis-1/2 md:odd:pr-gutter"
      >
        <Text class="text-sm"><strong>{review.displayName}</strong>, {review.createdAt}</Text>
        <div class="flex items-center justify-between py-2 md:justify-start">
          <TrustPilotStars rating={review.stars} class="h-4" />
          <div class="relative ml-gutter">
            {#if review.qualityRating !== undefined || review.valueForMoneyRating !== undefined}
              <button
                type="button"
                class="flex items-center"
                onclick={() => toggleMoreDetails(pos)}
              >
                <Text color="medium" class="pr-1 text-sm !text-st-gray-600">
                  {moreDetailsShowing.includes(pos)
                    ? translations.lessDetails
                    : translations.moreDetails}
                </Text>
                <div
                  class={clsx(
                    moreDetailsShowing.includes(pos) ? 'rotate-180' : '',
                    'h-3 text-st-gray-600 transition-transform',
                  )}
                >
                  <KeyboardArrowDown />
                </div>
              </button>
            {/if}
            <div
              class={clsx(
                moreDetailsShowing.includes(pos) ? 'flex' : 'hidden',
                'absolute -left-36 rounded-sm border border-st-gray-200 bg-st-white p-gutter shadow-popup md:-left-6',
              )}
            >
              {#if review.qualityRating !== undefined}
                <div class="mr-medium min-w-20 text-nowrap">
                  <Text size="small">{translations.quality}</Text>
                  <TrustPilotStars rating={review.qualityRating} />
                </div>
              {/if}
              {#if review.valueForMoneyRating !== undefined}
                <div class="min-w-20 text-nowrap">
                  <Text size="small">{translations.valueForMoney}</Text>
                  <TrustPilotStars rating={review.valueForMoneyRating} />
                </div>
              {/if}
            </div>
          </div>
        </div>
        <Paragraph>{review.content}</Paragraph>
      </li>
    {/each}
  </ul>
  {#if apiResponse.allReviews > reviews.length}
    <div class="flex justify-center pt-medium">
      <Button
        size="medium"
        onClick={() => loadMoreReviews()}
        color={loadMoreButtonColor ?? 'brand'}
      >
        {translations.moreReviews}
      </Button>
    </div>
  {/if}
</div>
